<template>
	<div class="row centered">
		<AppTable ref="receipts-table"
			v-if="userStore.currBusinessId"
			:fetch-function="administrationStore.getFilteredEpsonReceipts"
			:filters="filters"
			:table-headings="tableHeadings"
			:title="getString('fiscal_printer_receipts')"
			:total-items="administrationStore.businessEpsonReceiptsTotal || 0"
			:items="administrationStore.businessEpsonReceipts || []"
			initial-sort-field="date"
		  	allow-download
		    @export="exportReceipts"
			@failed-fetch="details=>{$emit('failed-fetch', details)}" >

			<template v-slot:epsonNumber="{item}">
				<div class="row centered">
					<p class="payment-intent" :class="{clickable: !!item.order_id}" @click="goToOrder(item.order_id)">{{ item.epsonNumber }}</p>
				</div>
			</template>

		</AppTable>
	</div>
</template>

<script>
import AppTable from "@/shared/components/AppTable.vue";
import {useUserStore, useAdministrationStore, useOrdersStore} from "@/console-new/stores";
import {useLanguageStore, useScriptStore, useCurrencyStore, useNotificationStore} from "@/shared/stores";
import AppButtonIcon from "@/shared/components/AppButtonIcon.vue";
import AppTableButtonGroup from "@/shared/components/AppTableButtonGroup.vue";
import {doubleFigure, formatDateOnly, formatDateTimeWithSeconds} from "@/shared/helpers/dates";
import {exportToFile} from "@/shared/helpers/excel";

export default {
	setup() {
		const userStore = useUserStore();
		const administrationStore = useAdministrationStore();
		const languageStore = useLanguageStore();
		const ordersStore = useOrdersStore();
		const scriptStore = useScriptStore();
		const currencyStore = useCurrencyStore();
		const notificationStore = useNotificationStore();
		return {userStore, administrationStore, getString: languageStore.getString, ordersStore, scriptStore, currencyStore, showNotification: notificationStore.showNotification};
	},
	name: "EpsonReceiptsTable",
	emits: ["failed-fetch"],
	components: {AppTableButtonGroup, AppButtonIcon, AppTable},
	data() {
		return {
			filters: [
				{
					type: "date",
					key: 'date',
					tableName: 'businesses_receipts_epson',
					text: this.getString('date'),
					finalValue: '',
					active: false
				},
				{
					type: "price",
					key: "total",
					text: this.getString('amount'),
					active: false,
					value: null,
					activeItem: 0,
					finalValue: '',
				},
				{
					type: "checklist",
					key: "status",
					text: this.getString('status'),
					values: [this.getString('emitted'), this.getString('refunded'), this.getString('canceled')],
					keysArray: ["emitted","refunded","canceled"],
					active: false,
					selectedIndexes: [],
					finalValue: '',
				},
			],
			tableHeadings: [
				{
					text: this.getString('receipt_number'),
					cols: 1,
					sortable: false,
					fieldName: 'epsonNumber',
					slotName: 'epsonNumber',
				},
				{
					text: this.getString('date'),
					cols: 1,
					sortable: true,
					fieldName: 'date',
					formatter: function(val) {
						return doubleFigure(val.getDate()) + '-' + doubleFigure(val.getMonth() + 1) + '-' + val.getFullYear();
					}
				},
				{
					text: this.getString('amount'),
					cols: 1,
					sortable: true,
					fieldName: 'total',
					formatter: 'currency'
				},
				{
					text: this.getString('type'),
					cols: 1,
					fieldName: 'type',
					formatter: payment_type => payment_type ? this.getString('payment_type_' + payment_type) : '-',
				},
				{
					text: this.getString('user'),
					cols: 1,
					sortable: true,
					fieldName: 'user_id',
					formatter: (val) => {
						return val && this.userStore.businessUsers && this.userStore.businessUsers.hasOwnProperty(val) ? this.userStore.businessUsers[val].name : '-';
					}
				},
				{
					text: this.getString('status'),
					cols: 1,
					fieldName: 'status',
					formatter: (val) => {
						return this.getString(val);
					}
				},
			],
			
		}
	},
	computed: {
	},
	methods: {
		goToOrder(orderId) {
			if(orderId) {
				this.ordersStore.search = "id:"+orderId;
				this.ordersStore.showCompleted = true;
				this.ordersStore.filterCounter = true;
				this.ordersStore.filterDelivery = true;
				this.ordersStore.filterTakeaway = true;
				this.ordersStore.filterRooms = true;
				this.ordersStore.refreshOrders();
				this.$router.push("/ops/orders");
			}
		},
		exportReceipts(expExtension) {
			let header = [];
			this.tableHeadings.forEach(heading => {
				if(heading.text) {
					header.push(heading.text);
				}
			});

			let today = new Date().toLocaleString();
			let businessNameNorm = this.userStore.currBusiness.name.toLowerCase().replace(/[^a-zA-Z0-9]/g, "");
			let fileName = businessNameNorm+"_"+this.getString('fiscal_printer_receipts').toLowerCase()+"_"+today.replace(", ", "_");
			exportToFile({
				columns: [
					{ header: header[0], key: 'epsonNumber' },
					{ header: header[1], value: r => formatDateTimeWithSeconds(r.date, false) },
					{ header: header[2], value: r => this.currencyStore.dbToActualValue(r.total), type: 'number', numberFormat: '#,##0.00' },
					{ header: header[3], value: r => this.getString('payment_type_' + r.type) },
					{ header: header[4], value: r => r.user_id ? (this.userStore.businessUsers && this.userStore.businessUsers.hasOwnProperty(r.user_id) ? this.userStore.businessUsers[r.user_id].name : '-') : '-' },
					{ header: header[5], value: r => this.getString(r.status) },
				],
				rows: this.administrationStore.businessEpsonReceipts,
				fileName,
				sheetName: this.getString("fiscal_printer_receipts"),
				format: expExtension,
				onEmpty: () => this.showNotification('table-no-data'),
			});
		}
	},
	watch: {
		'userStore.currBusinessId': {
			handler(newVal, oldVal) {
				if(newVal && newVal !== oldVal) {
					if(this.$refs['receipts-table']) {
						this.$refs['receipts-table'].fetchData();
					}
				}
			},
		},
	}
}
</script>


<style lang="scss" scoped>
a:after {
	content: none;
}

.no-download {
	font-size: 12px;
}

.payment-intent {
	text-align: left;
	width: 100%;
	font-size: 12px;

	&.clickable {
		cursor: pointer;
		color: var(--color-primary);
		text-decoration: underline;
	}
}
</style>
